import { RobotOutlined, UserOutlined } from '@ant-design/icons';
import MDEditor from '@uiw/react-md-editor';
import { Avatar, Typography } from 'antd';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import ChartRenderer, { AiChartSpec, parseChartSpecs } from './ChartRenderer';
import MentionCustomer from './MentionCustomer';
import MentionDepartment from './MentionDepartment';
import MentionUser from './MentionUser';
import MessageTrace from './MessageTrace';
import ThinkingIndicator from './ThinkingIndicator';
import { TraceMessage, flattenBatches, stripInlineToolMarkup } from './trace';
import './ai-assistant.css';
import {
  MENTION_PROTOCOLS,
  MentionSegment,
  hastText,
  parseMentionHref,
  rehypeStripMentionPrefix,
  splitMentions,
} from './mention';

/**
 * 默认 schema 只放行 http/https/mailto 等，会把提及用的 user:// 与 customer://
 * 链接剥成纯文本。这里仅额外放行这两个协议，其余（javascript: 等）仍被拦截。
 */
const mentionSanitizeSchema = {
  ...defaultSchema,
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href || []), ...Object.values(MENTION_PROTOCOLS)],
  },
};

/** 按协议分派到员工/客户/部门标签 */
function renderMention(kind: string, id: string, name: string) {
  if (kind === 'customer') {
    return <MentionCustomer name={name} customerId={id} />;
  }
  if (kind === 'department') {
    return <MentionDepartment name={name} departmentId={id} />;
  }
  return <MentionUser name={name} userId={id} />;
}

export type BubbleMessage = TraceMessage & {
  streaming?: boolean;
  /** 流式过程中收到的图表 spec（历史消息用 charts 字段回放） */
  liveCharts?: AiChartSpec[];
};

interface Props {
  message: BubbleMessage;
  /** 右侧用户气泡头像；对话页传当前用户头像，管理端回看别人会话时不传（回退默认图标） */
  userAvatarUrl?: string;
  /** 左侧助手气泡头像；对话页传公司 logo，取不到回退默认图标 */
  assistantAvatarUrl?: string;
}

const MessageBubble = ({ message, userAvatarUrl, assistantAvatarUrl }: Props) => {
  const isUser = message.role === 'USER';
  const charts = message.liveCharts ?? parseChartSpecs(message.charts);
  const bodyText = stripInlineToolMarkup(message.content);
  // 尚未出字的流式阶段用思考指示器占位；有工具在跑时文案说得更具体些。
  // 按可见文本判断：首个 delta 只有空白/换行时仍算「没出字」，否则指示器提前消失、光标也无处可挂
  const waiting = Boolean(message.streaming) && !bodyText.trim();
  const hasRunningTool = flattenBatches(message.toolBatches).some((item) => item.running);

  return (
    <div
      data-testid="message-bubble"
      style={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        gap: 8,
        marginBottom: 16,
      }}
    >
      {/* src 加载失败时 antd Avatar 自动回退到 icon，无需自行探测头像是否存在 */}
      <Avatar
        style={{ backgroundColor: isUser ? '#1677ff' : '#00b96b', flexShrink: 0 }}
        src={isUser ? userAvatarUrl : assistantAvatarUrl}
        icon={isUser ? <UserOutlined /> : <RobotOutlined />}
      />
      <div
        style={{
          maxWidth: '75%',
          minWidth: charts.length > 0 ? '60%' : undefined,
          padding: '8px 12px',
          borderRadius: 8,
          background: isUser ? '#e6f4ff' : '#f5f5f5',
          overflowX: 'auto',
        }}
      >
        {isUser ? (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {splitMentions(message.content || '').map((seg: MentionSegment, index) =>
              seg.type === 'mention' ? (
                <span key={index}>{renderMention(seg.kind!, seg.id!, seg.name!)}</span>
              ) : (
                <span key={index}>{seg.text}</span>
              ),
            )}
          </div>
        ) : (
          <div data-color-mode="light">
            {/* 技能、思考与工具调用收进折叠区，正文只留最终回答 */}
            <MessageTrace
              reasoning={message.liveReasoning ?? message.reasoningContent}
              toolBatches={message.toolBatches}
              skillTrace={message.skillTrace}
              streaming={message.streaming}
            />
            {waiting ? (
              <ThinkingIndicator text={hasRunningTool ? '正在查询数据' : '正在思考'} />
            ) : (
              // 流式中给正文最后一个块挂 CSS 闪烁光标（不再往 Markdown 源里拼静态 ▍）
              <div className={message.streaming ? 'ai-streaming-md' : undefined}>
                <MDEditor.Markdown
                  source={bodyText}
                  style={{ background: 'transparent' }}
                  // AI 输出/历史消息不可信：sanitize 防持久型 XSS（剥掉原始 HTML 与 javascript: 链接）。
                  // rehypeStripMentionPrefix 在 sanitize 之后运行，只删文本节点里的字面 @，不新增元素
                  rehypePlugins={[[rehypeSanitize, mentionSanitizeSchema], rehypeStripMentionPrefix]}
                  components={{
                    a: (props: any) => {
                      const { node, href, children, ...rest } = props;
                      const ref = parseMentionHref(href);
                      if (ref) {
                        return renderMention(ref.kind, ref.id, hastText(node));
                      }
                      return (
                        <a href={href} {...rest}>
                          {children}
                        </a>
                      );
                    },
                  }}
                />
              </div>
            )}
            {charts.map((spec, index) => (
              <ChartRenderer key={index} spec={spec} />
            ))}
            {message.status === 'FAILED' && (
              <Typography.Text type="danger">
                回复失败{message.errorMessage ? `：${message.errorMessage}` : '，请稍后重试'}
              </Typography.Text>
            )}
            {message.status === 'INTERRUPTED' && (
              <Typography.Text type="secondary">（回复已中断）</Typography.Text>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
